/**
 * The old link to the in-app design-system gallery.
 *
 * The gallery (`/platform/component-gallery`) was removed on 8 October 2026, as the employee app's was (wip-map
 * #520). Ricoh Paper (`@ricoh-paper/angular`) is the design system now, and its own docs site, the component viewer,
 * shows every component and theme. Bookmarks and links in old documents still point here, so the old URL sends the
 * browser on to that site.
 *
 * The viewer is the Cloud Run service `ricoh-paper-viewer` (rdx-platform-eu, europe-west1), deployed from
 * ricoh-paper's `cloudbuild/deploy-viewer.yaml` and served at the domain below. The site is public, so the route
 * carries no `authGuard` and sits outside the admin layout: no session is admitted on the way out, so the app makes
 * no identity reads (`App`'s `sessionAdmitted` gate).
 *
 * Framework-light: it imports no component, so `app.routes.ts` can use it without growing the initial bundle.
 */
import { DOCUMENT } from '@angular/common';
import { InjectionToken, inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';

/** The Ricoh Paper docs site (the component viewer). */
export const PAPER_DOCS_URL = 'https://paper.ricohworkplace.com/';

/** The old gallery URLs, as route paths, each answered by {@link toPaperDocs}. */
export const RETIRED_GALLERY_PATHS = ['platform/component-gallery'] as const;

/**
 * Leaves this app for another site, replacing the current history entry so Back does not bounce through the
 * redirect. A token so a spec can watch the hand-off instead of navigating jsdom.
 */
export const OPEN_OTHER_SITE = new InjectionToken<(url: string) => void>('OPEN_OTHER_SITE', {
  providedIn: 'root',
  factory: () => {
    const doc = inject(DOCUMENT);
    return (url: string) => doc.location.replace(url);
  },
});

/** Leaves for the Paper docs site and cancels the navigation here, so this app draws no page on the way out. */
export const toPaperDocs: CanActivateFn = () => {
  inject(OPEN_OTHER_SITE)(PAPER_DOCS_URL);
  return false;
};
